import Link from "@mui/material/Link";
import { endOfDay, isValid, parseISO, startOfDay } from "date-fns";
import { MRT_ColumnDef, MRT_FilterFn } from "material-react-table";
import { Link as RouterLink } from "react-router";

import routes from "../../../routes";

import { ProspectRow } from "../types";
import { matchesMultiSelect, MultiSelectData } from "./filterOptions";
import { MultiSelectFilter } from "./MultiSelectFilter";
import ProspectCell from "./ProspectCell";
import {
  prestoneReportField,
  ProspectFilterVariant,
  ProspectTableFieldDefinition,
  scoreFieldPrefix,
  scoreMaxValues,
} from "./model";

export const prospectViewPath = (prospectId: string) =>
  routes.prospects.view({ id: encodeURIComponent(prospectId) });

export type ProspectColumnMeta = {
  filterVariant: ProspectFilterVariant;
};

const getHeadCellSx = (fieldDefinition: ProspectTableFieldDefinition) => ({
  verticalAlign: "top",
  "& .Mui-TableHeadCell-Content": {
    alignItems: "flex-start",
    minHeight: "3.75rem",
  },
  "& .MuiCollapse-root": {
    marginTop: "auto",
    width: "100%",
  },
  "& .Mui-TableHeadCell-Content-Labels": {
    alignItems: "flex-start",
    overflow: "visible",
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  "& .Mui-TableHeadCell-Content-Wrapper": {
    whiteSpace: "normal !important",
    overflow: "visible !important",
    textOverflow: "clip !important",
  },
  ...(fieldDefinition.field.startsWith(scoreFieldPrefix) && {
    width: fieldDefinition.minWidth,
    maxWidth: fieldDefinition.minWidth,
  }),
  ...(fieldDefinition.filterVariant === "numberRange" && {
    "& .MuiCollapse-root .MuiBox-root": {
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    },
    "& .MuiCollapse-root .MuiFormControl-root": {
      minWidth: 0,
      width: "100%",
    },
  }),
});

const booleanFilter: MRT_FilterFn<ProspectRow> = (
  row,
  columnId,
  filterValue,
) => {
  if (filterValue !== "true" && filterValue !== "false") {
    return true;
  }

  return String(row.getValue(columnId)) === filterValue;
};

const toDate = (value: Date | number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const date = typeof value === "string" ? parseISO(value) : new Date(value);

  return isValid(date) ? date : undefined;
};

const toBoundaryTimestamp = (
  value: Date | number | string | null | undefined,
  boundary: "start" | "end",
) => {
  const date = toDate(value);

  if (!date) {
    return undefined;
  }

  return (boundary === "start" ? startOfDay(date) : endOfDay(date)).getTime();
};

const submissionTimeDateRangeFilter: MRT_FilterFn<ProspectRow> = (
  row,
  columnId,
  filterValue,
) => {
  const submissionTime = row.getValue<number | null>(columnId);

  if (typeof submissionTime !== "number" || Number.isNaN(submissionTime)) {
    return false;
  }

  const [rawStart, rawEnd] = Array.isArray(filterValue) ? filterValue : [];
  const start = toBoundaryTimestamp(
    rawStart as Date | number | string | null | undefined,
    "start",
  );
  const end = toBoundaryTimestamp(
    rawEnd as Date | number | string | null | undefined,
    "end",
  );

  if (start !== undefined && submissionTime < start) {
    return false;
  }

  if (end !== undefined && submissionTime > end) {
    return false;
  }

  return true;
};

submissionTimeDateRangeFilter.autoRemove = (value) =>
  !Array.isArray(value) || value.every((entry) => entry == null);

/**
 * Builds an MRT filter function for a multi-select column. The factory
 * captures `rawValuesByRowId` via closure so the filter can compare the
 * user's selection against the original (un-formatted) answer rather
 * than the joined display string on the row.
 */
const createMultiSelectFilter = (
  rawValuesByRowId: Map<string, Record<string, unknown>>,
): MRT_FilterFn<ProspectRow> => {
  const fn: MRT_FilterFn<ProspectRow> = (row, columnId, filterValue) => {
    if (!Array.isArray(filterValue)) {
      return true;
    }
    const rawValues = rawValuesByRowId.get(row.original.id);
    return matchesMultiSelect(rawValues?.[columnId], filterValue as string[]);
  };
  fn.autoRemove = (value) => !Array.isArray(value) || value.length === 0;
  return fn;
};

const getFilterFunction = (
  filterVariant: ProspectFilterVariant,
  multiSelectFilter: MRT_FilterFn<ProspectRow>,
): MRT_ColumnDef<ProspectRow>["filterFn"] => {
  switch (filterVariant) {
    case "none":
      return undefined;
    case "dateRange":
      return submissionTimeDateRangeFilter;
    case "numberRange":
      return "betweenInclusive";
    case "boolean":
      return booleanFilter;
    case "multiSelect":
      return multiSelectFilter;
    case "text":
    default:
      return "contains";
  }
};

const getFilterVariant = (
  filterVariant: ProspectFilterVariant,
): MRT_ColumnDef<ProspectRow>["filterVariant"] => {
  switch (filterVariant) {
    case "dateRange":
      return "date-range";
    case "numberRange":
      return "range";
    case "boolean":
      return "checkbox";
    case "text":
      return "text";
    case "multiSelect":
    case "none":
    default:
      return undefined;
  }
};

export const createProspectColumns = (
  fieldDefinitions: ProspectTableFieldDefinition[],
  multiSelectData: MultiSelectData = {
    optionsByField: new Map(),
    rawValuesByRowId: new Map(),
  },
): MRT_ColumnDef<ProspectRow>[] => {
  const { optionsByField, rawValuesByRowId } = multiSelectData;
  const multiSelectFilter = createMultiSelectFilter(rawValuesByRowId);

  return fieldDefinitions.map((fieldDefinition) => {
    const scoreMax = fieldDefinition.field.startsWith(scoreFieldPrefix)
      ? scoreMaxValues[fieldDefinition.field.slice(scoreFieldPrefix.length)]
      : undefined;
    const isMultiSelect = fieldDefinition.filterVariant === "multiSelect";
    const options = isMultiSelect
      ? (optionsByField.get(fieldDefinition.field) ?? [])
      : [];

    return {
      id: fieldDefinition.field,
      header: fieldDefinition.headerName,
      accessorFn: (row) => row[fieldDefinition.field],
      Cell: ({ row }) => {
        const value = row.original[fieldDefinition.field];

        if (
          fieldDefinition.field === prestoneReportField &&
          typeof value === "string"
        ) {
          return (
            <Link
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
            >
              View report
            </Link>
          );
        }

        const cell = (
          <ProspectCell
            value={value}
            scoreMax={scoreMax}
            isSubmissionTime={fieldDefinition.isSubmissionTime}
            isExpandableText={fieldDefinition.isExpandableText}
          />
        );

        return fieldDefinition.field === "projectName" ? (
          <Link
            component={RouterLink}
            to={prospectViewPath(row.original.id)}
            color="inherit"
            underline="hover"
            sx={{ display: "block", width: "100%" }}
          >
            {cell}
          </Link>
        ) : (
          cell
        );
      },
      sortingFn: fieldDefinition.type === "number" ? "basic" : "alphanumeric",
      enableSorting: fieldDefinition.field !== prestoneReportField,
      filterFn: getFilterFunction(
        fieldDefinition.filterVariant,
        multiSelectFilter,
      ),
      filterVariant: getFilterVariant(fieldDefinition.filterVariant),
      enableColumnFilter: fieldDefinition.filterVariant !== "none",
      enableGlobalFilter:
        fieldDefinition.filterVariant === "text" ||
        fieldDefinition.filterVariant === "boolean" ||
        fieldDefinition.filterVariant === "multiSelect",
      enableHiding: fieldDefinition.field !== "projectName",
      enablePinning: fieldDefinition.field === "projectName",
      enableColumnOrdering: fieldDefinition.field !== "projectName",
      enableColumnDragging: fieldDefinition.field !== "projectName",
      meta: {
        filterVariant: fieldDefinition.filterVariant,
      } satisfies ProspectColumnMeta,
      size: fieldDefinition.minWidth,
      muiTableHeadCellProps: {
        sx: {
          ...getHeadCellSx(fieldDefinition),
          ...(fieldDefinition.field === "projectName" && {
            backgroundColor: "background.paper",
            opacity: 1,
          }),
        },
      },
      ...(isMultiSelect && {
        Filter: ({ column }) => (
          <MultiSelectFilter
            column={column}
            options={options}
            label={fieldDefinition.headerName}
          />
        ),
      }),
      ...(fieldDefinition.field.startsWith(scoreFieldPrefix) && {
        muiTableBodyCellProps: {
          sx: {
            width: fieldDefinition.minWidth,
            maxWidth: fieldDefinition.minWidth,
          },
        },
      }),
      ...(fieldDefinition.field === "projectName" && {
        muiTableBodyCellProps: {
          sx: {
            backgroundColor: "background.paper",
            opacity: 1,
          },
        },
      }),
    };
  });
};
